<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataTypeToShow"
          :data-type="card.dataType"
        />
      </v-row>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          v-for="(filter, index) in filtersObj"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            :data-type="card.dataType"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import ChannelFilter from './ChannelFilter';
import NegativationFilter from './NegativationFilter';
import PostByIdFilter from './PostByIdFilter';
import GenderFilter from './GenderFilter';
import PolarityFilter from './PolarityFilter';
import CheckedPosts from './CheckedPosts';
import UserFilter from './UserFilter';
import DarkPostFilter from './DarkPostFilter';

import { Flags } from '@/plugins/rollout';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import filtersModel from '@/mixins/filters-model';

import { isEqual, cloneDeep, differenceWith } from 'lodash';
import { mapGetters } from 'vuex';
import {
  DATA_TYPE,
  FILTER_NAMES,
} from '@/store/modules/cards/constants';

export default {
  name: 'FilterListeningModal',
  components: {
    StIcon,
    ChannelFilter,
    NegativationFilter,
    PostByIdFilter,
    GenderFilter,
    PolarityFilter,
    CheckedPosts,
    UserFilter,
    DarkPostFilter,
    GlobalFilterSummary,
  },
  mixins: [
    filtersModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appliedFilter: {},
      tempFilter: {},
      globalFilterByDataType: {},
      globalFilterByDataTypeToShow: {},
    };
  },
  computed: {
    filterLabel() {
      return this.$t('components.filter.ModalFilters.label.listening.title');
    },
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('filter', [
      'filterByDataType',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    filtersObj() {
      const themeItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.listening.notClassified'),
          value: 'Não Classificados',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.themes?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const tagItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.listening.notClassified'),
          value: 'Sem Tags',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.tags?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const namesItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.listening.notClassified'),
          value: 'Sem Grupos',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.names?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      return [
        {
          component: 'ChannelFilter',
          filterType: FILTER_NAMES.CHANNEL,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.CHANNEL),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.GROUP,
          props: {
            label: this.$t('components.filter.ModalFilters.label.listening.groups'),
            selectItems: namesItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.GROUP),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.THEME,
          props: {
            label: this.$t('components.filter.ModalFilters.label.listening.themes'),
            selectItems: themeItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.TAG),
          },
        },
        {
          component: 'PostByIdFilter',
          filterType: FILTER_NAMES.PIDS,
        },
        {
          component: 'GenderFilter',
          filterType: FILTER_NAMES.GENDER,
        },
        {
          component: 'PolarityFilter',
          filterType: FILTER_NAMES.POLARITY,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.POLARITY),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.TAG,
          props: {
            label: this.$t('components.filter.ModalFilters.label.listening.tags'),
            selectItems: tagItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.TAG),
            hasTooltip: true,
          },
        },
        {
          component: 'CheckedPosts',
          filterType: FILTER_NAMES.FAVORITE,
        },
        {
          component: 'UserFilter',
          filterType: FILTER_NAMES.UIDS,
          props: {
            selectedItems: this.activeDashboardFilters?.uids || {},
            itemId: this.card.id,
            clearable: this.displayClearable(FILTER_NAMES.UIDS),
          },
        },
        {
          component: 'DarkPostFilter',
          filterType: FILTER_NAMES.DARK_POST,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.DARK_POST),
          },
        },
      ];
    },
    filterTitleLabel() {
      return `${this.filterLabel} - ${this.card.props.title}`;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter));
    },
    showClearFilter() {
      return Object.keys(this.appliedFilter).length;
    },
  },
  created() {
    this.globalFilterByDataType = this.filterByDataType[this.card.dataType];
    this.globalFilterByDataTypeToShow = this.globalFilterByDataType;

    if ('filter' in this.card.props) {
      this.appliedFilter = cloneDeep(this.card.props.filter);
      this.tempFilter = cloneDeep(this.card.props.filter);

      const keysToUse = differenceWith(Object.keys(this.globalFilterByDataType), Object.keys(this.tempFilter), isEqual);

      this.globalFilterByDataTypeToShow = Object.fromEntries(Object.entries(this.globalFilterByDataType).filter(global => keysToUse.includes(global[0])));
    }
  },
  methods: {
    closeDialog() {
      this.$emit('input', false);
    },
    clearFilter() {
      this.$emit('applyFilter', {});
      this.closeDialog();
    },
    applyFilter() {
      // Sanatize before send
      for (const filter in this.tempFilter) {
        if (!this.tempFilter[filter] || (!this.tempFilter[filter]?.value && this.tempFilter[filter]?.value?.length)) {
          delete this.tempFilter[filter];
        }
      }
      this.$emit('applyFilter', this.tempFilter);
      this.closeDialog();
    },
    clearChangesFilter() {
      this.tempFilter = cloneDeep(this.appliedFilter);
    },
    generateObjectToFilter(value) {
      return {
        text: value,
        value,
      };
    },
    displayClearable(componentKey) {
      return Boolean(this.showGlobalFilterFeature && this.globalFilterByDataType?.hasOwnProperty(componentKey));
    },
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.LISTENING });
    },
  },
};
</script>

<style scoped lang="scss">
  .align-items-center {
    align-items: center;
  }
</style>
